import { defineStore } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';
import type { PagesConfig } from '@shared/types/PagesConfig';
import type { DetailLinkMode } from '@/Utils/Wc/detailLinks';
import type { WcRouterMode } from '@/Utils/Wc/routerConfig';
import { parseCsv } from '@/Utils/Core/list';

const DEFAULT_AVAILABLE_DATA_TYPES = [
  'Area',
  'Article',
  'City',
  'Event',
  'Gastro',
  'Hotel',
  'Package',
  'POI',
  'Tour',
] as const;

function getValuesFromCapabilities(capabilities: string[] | undefined, key: string): string[] {
  if (!capabilities?.length) return [];

  const values = capabilities
    .filter((capability) => {
      return capability.trim().toLowerCase().startsWith(`${key.toLowerCase()}:`);
    })
    .flatMap((capability) => {
      const [, raw = ''] = capability.split(':', 2);
      return parseCsv(raw);
    });

  return Array.from(new Set(values));
}

export const useContextStore = defineStore('context', {
  state: () => ({
    integrationMode: 'standalone' as 'standalone' | 'wc',
    detailLinkMode: 'default' as DetailLinkMode,
    wcRouterMode: 'memory' as WcRouterMode,
    wcRouterBasePath: '/',
    language: '',
    experience: '',
    template: '',
    view: '',
    licenseKey: null as string | null,
    availableDataTypes: [...DEFAULT_AVAILABLE_DATA_TYPES] as string[],
    features: [] as string[],
  }),
  getters: {
    isWcIntegration(): boolean {
      return this.integrationMode === 'wc';
    },
    isKite(): boolean {
      return this.template === 'terminal' || this.template === 'tv';
    },
    hasFeature: (state) => {
      return (featureKey: string): boolean => {
        if (!featureKey) {
          return false;
        }

        const wanted = String(featureKey).trim().toLowerCase();
        return (state.features ?? []).some((feature) => {
          return String(feature).trim().toLowerCase() === wanted;
        });
      };
    },
  },
  actions: {
    applyCapabilitiesFromConfig(config: PagesConfig | null) {
      const capabilities = config?.pages2?.capabilities;
      const disabledTypes = getValuesFromCapabilities(capabilities, 'disabledTypes');
      const disabledTypesSet = new Set(disabledTypes.map((type) => type.toLowerCase()));
      this.features = getValuesFromCapabilities(capabilities, 'features');

      this.availableDataTypes = DEFAULT_AVAILABLE_DATA_TYPES.filter(
        (type) => !disabledTypesSet.has(type.toLowerCase()),
      );
    },
    init() {
      const parts = window.location.pathname.split('/').filter(Boolean);
      this.language = parts[0] || '';
      this.experience = parts[1] || '';
      this.template = parts[2] || '';
      this.view = parts[3] || '';
    },
    updateFromRoute(route: RouteLocationNormalized) {
      if (route.params) {
        this.language = (route.params.language as string) || this.language;
        this.experience = (route.params.experience as string) || this.experience;
        this.template = (route.params.template as string) || this.template;
        this.view = typeof route.name === 'string' ? route.name.toLowerCase() : this.view;
      }
    },
  },
});
